import { gql, useQuery } from '@apollo/client';

import { NexoyaQuery, NexoyaQueryTeamArgs } from '../../types/types';
import { useTeam } from '../../context/TeamProvider';

import { RETRYABLE_CONTEXT } from '../retryLink';
import { ORGANIZATION_WITH_TENANT_FRAGMENT } from '../organization/fragments';
import { TEAM_META_FRAGMENT } from './fragments';
import { Currency, useCurrencyStore } from 'store/currency-selection';
import useOrganizationStore, { DEFAULT_UI_CUSTOMIZATION_FALLBACK } from '../../store/organization';

const TEAM_QUERY = gql`
  query Team($team_id: Int!, $withMembers: Boolean!, $withOrg: Boolean!) {
    team(team_id: $team_id) {
      ...teamMeta
      members @include(if: $withMembers) {
        user_id
        firstname
        lastname
        email
        lastLogin
        role {
          name
        }
      }
      organization @include(if: $withOrg) {
        ...organizationWithTenant
      }
      onboarding
      featureFlags {
        name
        status
      }
      dashboardUrls {
        name
        url
      }
      customization
    }
    roles {
      name
      description
    }
  }
  ${TEAM_META_FRAGMENT}
  ${ORGANIZATION_WITH_TENANT_FRAGMENT}
`;
type Options = {
  withMembers?: boolean;
  withOrg?: boolean;
  skip?: boolean;
};

function useTeamQuery({ withMembers = false, withOrg = true, skip = false }: Options = {}) {
  const { teamId } = useTeam();
  const { setTeamCurrencySettings } = useCurrencyStore();
  // Selectors, not the whole store: an unselected `useOrganizationStore()` re-renders every
  // consumer of this hook on any store write, and `useTenantSync` now writes on team switch.
  const setOrganization = useOrganizationStore((s) => s.setOrganization);
  const setCustomization = useOrganizationStore((s) => s.setCustomization);

  const query = useQuery<
    Pick<NexoyaQuery, 'team' | 'roles'>,
    NexoyaQueryTeamArgs & { withMembers?: boolean; withOrg?: boolean }
  >(TEAM_QUERY, {
    skip,
    variables: {
      team_id: teamId,
      withMembers,
      withOrg,
    },
    // One failed fetch used to pin the tenant brand at null for the whole session — no retry,
    // no poll, no refetch — which is how a paying Nexoya team lost its branded upsell CTA.
    context: RETRYABLE_CONTEXT,
    onCompleted: (data) => {
      if (data?.team?.currency) {
        setTeamCurrencySettings({
          teamId,
          currency: data.team.currency as Currency,
          numberFormat: data.team.number_format,
        });
      }
      // The TENANT BRAND is deliberately not written here. `onCompleted` does not fire for a
      // cache-only read, so it kept the last team's answer on a team switch — see
      // `entitlements/useTenantSync.ts`, which derives it during render instead.
      if (data?.team?.organization && withOrg) {
        setOrganization(data?.team?.organization);
        setCustomization(data?.team?.organization?.tenant?.uiCustomization || DEFAULT_UI_CUSTOMIZATION_FALLBACK);
      }
    },
  });
  return query;
}

export { TEAM_QUERY, useTeamQuery };
